import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Search, Calendar, Languages } from 'lucide-react';
import { NAV_ITEMS } from '../../constants';
import { newsApi } from '../../utils/api';
import { useSiteSettings } from '../../hooks/useSiteSettings';

function MiniNav({ activeCat, onNavClick, hidden, onGoHome, onOpenArticle }) {
  const settings = useSiteSettings();
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState('');
  const [suggest, setSuggest] = useState([]);
  const lastNoResult = useRef('');

  // Live search suggestions (debounced) — natija nabhaye toast dekhauchha
  useEffect(() => {
    if (!searchOpen || q.trim().length < 2) {
      setSuggest([]);
      return undefined;
    }
    const term = q.trim();
    const t = setTimeout(async () => {
      try {
        const res = await newsApi.getAll({ q: term, limit: 6 });
        const list = res.data || [];
        setSuggest(list);
        if (list.length === 0 && lastNoResult.current !== term) {
          lastNoResult.current = term;
          toast.error(`«${term}» — कुनै समाचार फेला परेन।`);
        }
      } catch {
        setSuggest([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, searchOpen]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      setSuggest([]);
      onNavClick('search', q.trim());
    }
  };

  const pickSuggestion = (n) => {
    setSuggest([]);
    setQ('');
    setSearchOpen(false);
    if (onOpenArticle) onOpenArticle(n);
  };

  return (
   <nav
  className={`sticky top-0 z-30 bg-green-400 border-b border-green-600 transition-all duration-300 hidden lg:block ${
    hidden ? 'shadow-md' : ''
  }`}
>
      <div className="max-w-[1240px] mx-auto px-3 lg:px-5 flex items-center gap-0 lg:gap-2 min-h-[52px]">
        {/* Logo appears on the left when the header slides up (hidden) */}
        <button
          className={`items-center gap-1.5 flex-shrink-0 ${hidden ? 'flex' : 'hidden'}`}
          onClick={onGoHome}
          aria-label="Kalo Jhola गृहपृष्ठ"
        >
          {settings.mininav_logo ? (
            <span className="flex items-center justify-center w-[54px] h-[54px] rounded-full bg-white shadow-sm ring-1 ring-white/60">
              <img src={settings.mininav_logo} alt="Kalo Jhola" className="h-11 w-auto object-contain" />
            </span>
          ) : (
            <span className="font-display text-[17px] font-bold text-ink whitespace-nowrap hidden sm:inline">
              KALO<em className="not-italic text-crimson">JHOLA</em>
            </span>
          )}
        </button>

        {/* Mini nav buttons: desktop only, centered */}
        <div className="hidden lg:flex gap-1 overflow-x-auto no-scrollbar flex-1 justify-center">
          {NAV_ITEMS.map((c) => (
            <button
              key={c.id}
              className={`flex-shrink-0 px-4 py-2.5 text-[13.5px] font-semibold rounded-full whitespace-nowrap transition-colors ${
                activeCat === c.id
                  ? 'bg-crimson text-white'
                  : 'text-ink-soft hover:text-ink hover:bg-white'
              }`}
              onClick={() => onNavClick(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Tools: desktop view only */}
        <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
          <div className="relative">
            <form
              className={`flex items-center ${searchOpen ? 'bg-white border border-line rounded-full px-2' : ''}`}
              onSubmit={submitSearch}
            >
              <button
                type="button"
                className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] text-ink hover:bg-white"
                aria-label="Search"
                onClick={() => setSearchOpen((s) => !s)}
              >
                <Search size={16} />
              </button>
              {searchOpen && (
                <input
                  type="text"
                  placeholder="खोज्नुहोस्..."
                  className="bg-transparent outline-none text-[13px] w-[150px] px-1.5 py-1.5 text-ink"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  autoFocus
                />
              )}
            </form>

            {/* Suggestions with photos */}
            {searchOpen && suggest.length > 0 && (
              <div className="absolute right-0 top-[46px] w-[340px] bg-white border border-line rounded-xl shadow-lg overflow-hidden z-50">
                {suggest.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => pickSuggestion(n)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-paper-soft transition-colors border-b border-line last:border-b-0"
                  >
                    <img
                      src={n.image || `https://picsum.photos/seed/n${n.id}/120/90`}
                      alt=""
                      loading="lazy"
                      className="w-14 h-10 object-cover rounded-md flex-shrink-0"
                    />
                    <span className="min-w-0">
                      <span className="block text-[12.5px] font-semibold leading-snug line-clamp-2">{n.title}</span>
                      <span className="block text-[11px] text-muted mt-0.5">{n.category_name || 'समाचार'}</span>
                    </span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={submitSearch}
                  className="w-full text-center text-[12px] font-bold text-crimson py-2 hover:bg-paper-soft transition-colors"
                >
                  «{q.trim()}» सबै नतिजा हेर्नुहोस्
                </button>
              </div>
            )}
          </div>
          <button
            className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] text-ink hover:bg-white"
            aria-label="पात्रो"
            onClick={() => onNavClick('calendar')}
          >
            <Calendar size={17} />
          </button>
          <button
            className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] text-ink hover:bg-white"
            aria-label="युनिकोड कन्भर्टर"
            onClick={() => onNavClick('unicode')}
          >
            <Languages size={17} />
          </button>
        </div>
      </div>
    </nav>
  );
}

export default MiniNav;
